import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Search, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDevices } from '@/hooks/useDevices';
import { useEndpoints, type Endpoint } from '@/hooks/useEndpoints';
import { type DeviceSummary } from '@/hooks/useDevices';
import NetworkEquipmentManager from '@/components/NetworkEquipmentManager';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { cn } from '@/lib/utils';
import { canonicalIp } from '@/utils/netValidation';

/**
 * MyNetwork — unified inventory landing per addendum §1.4.
 *
 * Tabs:
 *   equipment  — switches / routers / APs / firewalls (devices, NOT endpoints)
 *   computers  — workstations, wireless clients, VoIP phones (endpoints)
 *   printers   — printers / copiers / MFPs (endpoints)
 *   other      — IoT + unidentified (endpoints)
 *   all        — every group, stacked
 *
 * The split between "endpoints" (devices we observe on the network) and
 * "equipment" (devices we actively manage) is preserved from the Bulma
 * original — they're separate hooks against separate endpoints. The
 * tabs sit above one or both ResponsiveTables depending on selection.
 *
 * The equipment tab IS the management surface (NetworkEquipmentManager:
 * add/activate/edit/delete) — the old standalone /devices list page
 * redirects here. It leads, and is the default, because managing gear is
 * why people open this page; the endpoint tabs are for looking things up.
 * The "All" tab keeps the slim read-only equipment table below, which
 * links to ?tab=equipment for management.
 *
 * Search filters both lists (server-side for endpoints, client-side for
 * equipment). Enter or Search button commits the input; the X clears.
 */

type Tab = 'computers' | 'printers' | 'equipment' | 'other' | 'all';

interface TabSpec {
  id: Tab;
  label: string;
  shortLabel: string;
  blurb: string;
}

const TABS: TabSpec[] = [
  {
    id: 'equipment',
    label: 'Network equipment',
    shortLabel: 'Equipment',
    blurb: 'The switches, routers, access points, and firewalls that run your network.',
  },
  {
    id: 'computers',
    label: 'Computers & phones',
    shortLabel: 'Computers',
    blurb: 'Laptops, desktops, servers, mobile devices, and VoIP phones on your network.',
  },
  {
    id: 'printers',
    label: 'Printers',
    shortLabel: 'Printers',
    blurb: 'Printers, copiers, and multi-function devices on your network.',
  },
  {
    id: 'other',
    label: 'IoT & other',
    shortLabel: 'Other',
    blurb: 'Cameras, smart-home gear, and anything we couldn’t identify automatically.',
  },
  {
    id: 'all',
    label: 'All',
    shortLabel: 'All',
    blurb: 'Everything on your network, grouped by category.',
  },
];

// Equipment leads the tab row and is what you land on with no ?tab= param.
const DEFAULT_TAB: Tab = 'equipment';

const COMPUTERS_CLASSES = ['workstation', 'server', 'wireless_client', 'voip_phone'];
const PRINTERS_CLASSES = ['printer'];
const IOT_OTHER_CLASSES = ['camera', 'iot', 'unknown'];
const PAGE_SIZE = 50;

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${String(sec)}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${String(min)}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${String(hr)}h ago`;
  return `${String(Math.floor(hr / 24))}d ago`;
}

function describeEndpoint(ep: Endpoint): string {
  return ep.hostname ?? ep.macAddress;
}

function EndpointLocationCell({ ep }: { ep: Endpoint }) {
  // This endpoint *is* a managed device — link to it rather than a port.
  if (ep.managedDevice) {
    return (
      <Link
        to={`/devices/${ep.managedDevice.id}`}
        className="hover:underline truncate"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {ep.managedDevice.name ?? ep.managedDevice.id}
        <span className="text-muted-foreground text-xs"> (device)</span>
      </Link>
    );
  }
  // Prefer the resolved attachment; fall back to a raw sighting.
  const cl = ep.computedLocation;
  const loc = cl ?? (ep.currentLocations.length > 0 ? ep.currentLocations[0] : null);
  if (!loc) {
    return <span className="text-muted-foreground">—</span>;
  }
  // Show only the single best-guess attachment — not the raw sighting count.
  // "~" marks an assumed fewest-MAC (edge) port.
  return (
    <span
      className="inline-flex items-center gap-1 min-w-0"
      title={cl?.method === 'fdb_edge' ? 'Assumed — fewest-MAC (edge) port' : undefined}
    >
      <Link
        to={`/devices/${loc.deviceId}`}
        className="hover:underline truncate"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {loc.deviceName ?? loc.deviceId}
      </Link>
      {loc.interfaceName ? (
        <span className="text-muted-foreground font-mono text-xs">
          :{loc.interfaceName}
        </span>
      ) : null}
      {cl?.method === 'fdb_edge' ? (
        <span className="text-muted-foreground text-xs flex-shrink-0" aria-hidden>~</span>
      ) : null}
    </span>
  );
}

// ─── Endpoint table (computers / printers / iot) ──────────────────────

interface EndpointTableProps {
  classes: string[];
  search: string;
}

function EndpointTable({ classes, search }: EndpointTableProps) {
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const params = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      classifications: classes.join(','),
      // Devices we manage show under "Network equipment", not as loose
      // endpoints — so an endpoint that is itself a managed device is hidden
      // here regardless of how it's classified.
      excludeManaged: true,
    }),
    [page, search, classes],
  );

  const { data, isLoading, error } = useEndpoints(params);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const columns = useMemo<readonly ResponsiveTableColumn<Endpoint>[]>(
    () => [
      {
        key: 'name',
        label: 'Name',
        hideMobileLabel: true,
        render: (ep) => (
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/endpoints/${ep.id}`}
              className="font-medium hover:underline truncate"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {describeEndpoint(ep)}
            </Link>
            {ep.classification ? (
              <Badge variant="secondary" className="text-[10px] font-normal capitalize flex-shrink-0">
                {ep.classification.replace(/_/g, ' ')}
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'mac',
        label: 'MAC',
        render: (ep) => (
          <code className="font-mono text-xs text-muted-foreground">{ep.macAddress}</code>
        ),
      },
      {
        key: 'vendor',
        label: 'Vendor',
        hideAt: 'mobile',
        render: (ep) =>
          ep.ouiVendor ?? <span className="text-muted-foreground">Unknown</span>,
      },
      {
        key: 'where',
        label: 'Where',
        render: (ep) => <EndpointLocationCell ep={ep} />,
      },
      {
        key: 'lastSeen',
        label: 'Last seen',
        hideAt: 'mobile',
        render: (ep) => (
          <span className="text-xs text-muted-foreground">{formatRelative(ep.lastSeenAt)}</span>
        ),
      },
    ],
    [],
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {data ? (
        <p className="text-xs text-muted-foreground">
          {String(data.total)} {data.total === 1 ? 'device' : 'devices'}
        </p>
      ) : null}

      <ResponsiveTable<Endpoint>
        columns={columns}
        rows={data?.endpoints ?? []}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/endpoints/${r.id}`); }}
        empty={
          <EmptyState
            variant="no-data"
            title="Nothing here yet"
            description="As things connect, they'll show up automatically."
            size="inline"
          />
        }
      />

      {data && totalPages > 1 ? (
        <nav
          className="flex items-center justify-between pt-1"
          aria-label="Pagination"
        >
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {String(page)} of {String(totalPages)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); }}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </div>
  );
}

// ─── Slim network equipment table (read-only, "All" tab only) ─────────
// The equipment tab renders the full NetworkEquipmentManager instead;
// this variant stays lean so the stacked "All" view isn't crowded with
// management affordances.

function NetworkEquipmentTable({ search }: { search: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: devices, isLoading, error } = useDevices(user?.organizationId ?? undefined);

  const filtered = useMemo<DeviceSummary[]>(() => {
    if (!devices) return [];
    if (!search) return devices;
    const lower = search.toLowerCase();
    // A whole address typed in another spelling still finds the stored one.
    const ip = canonicalIp(search) ?? lower;
    return devices.filter(
      (d) =>
        d.name.toLowerCase().includes(lower) ||
        (d.ipAddress?.toLowerCase().includes(ip) ?? false) ||
        d.deviceType.toLowerCase().includes(lower) ||
        d.regionName.toLowerCase().includes(lower),
    );
  }, [devices, search]);

  const columns = useMemo<readonly ResponsiveTableColumn<DeviceSummary>[]>(
    () => [
      {
        key: 'name',
        label: 'Name',
        hideMobileLabel: true,
        render: (d) => (
          <span className="flex items-center gap-2 min-w-0">
            <Link
              to={`/devices/${d.id}`}
              className="font-medium hover:underline truncate"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {d.name}
            </Link>
            {d.pollingPaused ? (
              <span
                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-sev-3-soft text-sev-3 whitespace-nowrap"
                title="Polling is paused for this device — either an operator held it off, or the account is over its plan's device limit. The device is still counted."
              >
                Monitoring paused
              </span>
            ) : null}
          </span>
        ),
      },
      { key: 'type', label: 'Type', render: (d) => d.deviceType },
      {
        key: 'address',
        label: 'Address',
        render: (d) =>
          d.ipAddress ? (
            <code className="font-mono text-xs">{d.ipAddress}</code>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'location',
        label: 'Location',
        hideAt: 'mobile',
        render: (d) => d.regionName,
      },
    ],
    [],
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {String(filtered.length)}{' '}
          {filtered.length === 1 ? 'piece of equipment' : 'pieces of equipment'}
        </p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/network?tab=equipment">Manage equipment</Link>
        </Button>
      </div>

      <ResponsiveTable<DeviceSummary>
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/devices/${r.id}`); }}
        empty={
          <EmptyState
            variant="no-data"
            title="No network equipment yet"
            description={
              <>
                <Link to="/network?tab=equipment" className="text-primary hover:underline">
                  Add some
                </Link>{' '}
                to get started.
              </>
            }
            size="inline"
          />
        }
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────

export default function MyNetwork() {
  // Active tab lives in the URL (?tab=…) so it survives reload, back/forward,
  // and link-sharing. The default tab (equipment) drops the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = TABS.some((t) => t.id === tabParam) ? (tabParam as Tab) : DEFAULT_TAB;
  // The <Tabs> below uses activationMode="manual" (see its comment), so
  // onValueChange fires exactly once per selection — no dedupe hack needed.
  const setActiveTab = (tab: Tab) => {
    if (tab === activeTab) return; // skip no-op (don't push a redundant entry)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (tab === DEFAULT_TAB) params.delete('tab');
        else params.set('tab', tab);
        return params;
      },
      { replace: false },
    );
  };
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const tabSpec = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  const commitSearch = () => { setSearch(searchInput.trim()); };
  const clearSearch = () => { setSearch(''); setSearchInput(''); };

  return (
    <PageContainer>
      <PageHeader
        title="My Network"
        subtitle="Everything on your network — the gear, the people, and their stuff."
      />

      {/* Search bar — input + Search button + (conditional) Clear */}
      <div className="flex items-center gap-2 mb-5 max-w-xl">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Search by name, MAC, vendor, IP…"
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitSearch();
              }
            }}
            className="pl-9 pr-9"
          />
          {search ? (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
        <Button onClick={commitSearch} disabled={!searchInput.trim()}>
          Search
        </Button>
      </div>

      {/* Tabs default to activationMode="manual" (see tabs.tsx) so this
          URL-synced tab doesn't double-push history on click. */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as Tab); }}>
        {/* Tabs scroll horizontally on phone — five tabs is too many for
            a 360px row, but the labels are short on mobile via shortLabel. */}
        <TabsList className="w-full overflow-x-auto justify-start">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="flex-shrink-0">
              <span className="hidden md:inline">{t.label}</span>
              <span className="md:hidden">{t.shortLabel}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <p className={cn('text-xs text-muted-foreground mb-4', 'mt-3')}>
          {tabSpec.blurb}
        </p>

        <TabsContent value="computers">
          <EndpointTable classes={COMPUTERS_CLASSES} search={search} />
        </TabsContent>
        <TabsContent value="printers">
          <EndpointTable classes={PRINTERS_CLASSES} search={search} />
        </TabsContent>
        <TabsContent value="equipment">
          <NetworkEquipmentManager search={search} />
        </TabsContent>
        <TabsContent value="other">
          <EndpointTable classes={IOT_OTHER_CLASSES} search={search} />
        </TabsContent>
        <TabsContent value="all" className="space-y-8">
          <section>
            <h2 className="text-base font-semibold mb-3">Network equipment</h2>
            <NetworkEquipmentTable search={search} />
          </section>
          <section>
            <h2 className="text-base font-semibold mb-3">Computers &amp; phones</h2>
            <EndpointTable classes={COMPUTERS_CLASSES} search={search} />
          </section>
          <section>
            <h2 className="text-base font-semibold mb-3">Printers</h2>
            <EndpointTable classes={PRINTERS_CLASSES} search={search} />
          </section>
          <section>
            <h2 className="text-base font-semibold mb-3">IoT &amp; other</h2>
            <EndpointTable classes={IOT_OTHER_CLASSES} search={search} />
          </section>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
